/**
 * Centralized API error handler
 *
 * When an API call fails:
 * 1. Check if the backend is reachable via the status endpoint
 * 2. If status fails: "The database seams to be down, talk to the administrator"
 * 3. If status succeeds: "Database service OK. Please review the input - you are trying to give me slop and I won't take it!"
 */

import { statusService } from '@/api'
import { useUIStore } from '@/stores/uiStore'

interface ApiError {
  detail?: string
  status?: number
}

/**
 * Handle API errors with backend health check
 * This function checks if the backend is up when an API call fails.
 *
 * @param error - The error from the failed API call
 * @param fallbackTitle - Title to use in the toast if backend is healthy
 */
export async function handleApiError(
  _error: ApiError,
  fallbackTitle: string = 'Request failed'
): Promise<void> {
  // 401 is handled by the API client interceptor (logout + redirect)
  if (_error?.status === 401) return

  const { addToast } = useUIStore.getState()

  try {
    // Check if backend is reachable
    const isHealthy = await statusService.isHealthy()

    if (!isHealthy) {
      // Backend is down or unhealthy
      addToast({
        title: 'Service Unavailable',
        description: 'The database seams to be down, talk to the administrator',
        variant: 'database_down',
      })
    } else {
      // Backend is up, but the request failed (likely validation/input error)
      addToast({
        title: fallbackTitle,
        description: "Database service OK. Please review the input - you are trying to give me slop and I won't take it!",
        variant: 'input_error',
      })
    }
  } catch {
    // Status check itself failed - backend is definitely down
    addToast({
      title: 'Service Unavailable',
      description: 'The database seams to be down, talk to the administrator',
      variant: 'database_down',
    })
  }
}

/**
 * Create an onError handler for React Query mutations
 *
 * @param fallbackTitle - Title to use in the toast if backend is healthy
 */
export function createMutationErrorHandler(fallbackTitle: string = 'Request failed') {
  return (error: ApiError) => handleApiError(error, fallbackTitle)
}

/**
 * Create an onError handler for React Query queries
 *
 * @param fallbackTitle - Title to use in the toast if backend is healthy
 */
export function createQueryErrorHandler(fallbackTitle: string = 'Failed to load data') {
  return (error: ApiError) => handleApiError(error, fallbackTitle)
}
